import React, { useState } from "react";
import PhoneInput, { PhoneInputProps } from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import {
  isSupportedCountry,
  parsePhoneNumberFromString,
  type CountryCode,
} from "libphonenumber-js";

type PhoneNumberFieldProps = {
  label: React.ReactNode;
  error?: string;
  required?: boolean;
  className?: string;
  inputClassName?: string;
  buttonClassName?: string;
  disableValidation?: boolean;
} & Omit<PhoneInputProps, "inputClass" | "containerClass" | "specialLabel">;

const dropdownClasses =
  "!bg-white dark:!bg-[#0A0A0A] !text-black dark:!text-[#EFDBD3] !border !border-[#999] dark:!border-[#595959] !rounded-[8px] !shadow-lg gw-phone-dropdown";

const PhoneNumberField: React.FC<PhoneNumberFieldProps> = ({
  label,
  error: externalError,
  required = false,
  className,
  inputClassName = "",
  buttonClassName = "",
  value,
  onChange,
  disableValidation = false,
  ...rest
}) => {
  const [localError, setLocalError] = useState<string | undefined>();

  const handleChange: PhoneInputProps["onChange"] = (rawValue, country, e, formattedValue) => {
    if (!onChange) return;

    const dialCode = (country as any)?.dialCode as string | undefined;
    const countryCode = (country as any)?.countryCode as string | undefined;

    const rawValueStr = String(rawValue ?? "");
    const nationalNumber =
      dialCode && rawValueStr.startsWith(dialCode)
        ? rawValueStr.slice(dialCode.length)
        : dialCode && dialCode.startsWith(rawValueStr)
          ? ""
          : rawValueStr;

    // Use libphonenumber-js to validate the number
    let validationError: string | undefined;
    const upperCountryCode = countryCode?.toUpperCase();

    // Trigger validation if there's at least some national number OR if we have a dial code we want to ensure's followed by a number
    if (!disableValidation && dialCode && upperCountryCode && isSupportedCountry(upperCountryCode)) {
      const phoneNumber = parsePhoneNumberFromString(
        `+${dialCode}${nationalNumber}`,
        upperCountryCode as CountryCode,
      );

      if (!phoneNumber?.isValid()) {
        // Only show "Invalid" if the user has started typing or if we want to be strict
        if (nationalNumber.length > 0) {
          validationError = "Invalid phone number for selected country";
        }
      }
    }

    setLocalError(validationError);

    const valueWithCountryCode = dialCode
      ? `+${dialCode}${nationalNumber ? `${nationalNumber}` : ""}`
      : rawValueStr;

    onChange(valueWithCountryCode, country, e, formattedValue);
  };

  const displayError = externalError || localError;

  return (
    <div className={className}>
      <label className="mb-1 block text-sm font-medium dark:text-[#EDE5DF]">
        {label}
        {required && <span className="ml-0.5 text-red-500">*</span>}
      </label>
      <PhoneInput
        value={value}
        placeholder="Enter your phone number"
        onChange={handleChange}
        specialLabel=""
        country={rest.country || "in"}
        containerClass="w-full"
        inputClass={`!w-full !rounded-[8px] !border !pr-3 !py-2 !text-sm focus:!outline-none focus:!ring-1 focus:!ring-orange-500 ${displayError ? "!border-red-500" : ""
          } ${inputClassName}`}
        buttonClass={`${buttonClassName} ${displayError ? "!border-red-500" : ""} `}
        dropdownClass={dropdownClasses}
        inputProps={{
          required,
          ...rest.inputProps,
        }}
        {...rest}
      />
      {displayError && <p className="mt-1 text-xs text-red-500">{displayError}</p>}
    </div>
  );
};

export default PhoneNumberField;
